import { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHomepage } from '@/contexts/homepage-context';
import { useSupinFields, resetSupinFields } from './supinFieldsStore';

// Always-mounted syncer: publishes the shared field values up to
// homepage-context (supinParamsReady + supinParams) so Generate stays wired no
// matter whether the "Inlet Parameters" window is open or closed — mirrors
// TubeframeParamsSync exactly.
export function SupinParamsSync() {
  const { supinInletType, setSupinParamsReady, setSupinParams } = useHomepage();
  const {
    mach,
    altitude,
    qdyn,
    engineFlow,
    rampStages,
    rampAngle,
    coneAngle,
    numStages,
    parentMach,
    kstex,
    cfdGrids,
  } = useSupinFields();
  const usesQdyn = supinInletType === 'K5';

  useEffect(() => {
    const hasType = supinInletType !== '';
    const heightOk = usesQdyn ? qdyn.trim() !== '' : altitude.trim() !== '';
    const ready =
      hasType && mach.trim() !== '' && heightOk && engineFlow.trim() !== '';
    setSupinParamsReady(ready);
    if (ready) {
      setSupinParams({
        inletType: supinInletType as 'K1' | 'K2' | 'K3' | 'K4' | 'K5',
        mach: mach.trim(),
        ...(usesQdyn ? { qdyn: qdyn.trim() } : { altitude: altitude.trim() }),
        engineFlow: engineFlow.trim(),
        ...(supinInletType === 'K2' || supinInletType === 'K4'
          ? { rampStages: rampStages.trim(), rampAngle: rampAngle.trim() }
          : {}),
        ...(supinInletType === 'K3'
          ? { coneAngle: coneAngle.trim(), numStages: numStages.trim() }
          : {}),
        ...(supinInletType === 'K5'
          ? { parentMach: parentMach.trim(), kstex: kstex.trim() }
          : {}),
        cfdGrids,
      });
    } else {
      setSupinParams(null);
    }
  }, [
    supinInletType,
    usesQdyn,
    mach,
    altitude,
    qdyn,
    engineFlow,
    rampStages,
    rampAngle,
    coneAngle,
    numStages,
    parentMach,
    kstex,
    cfdGrids,
    setSupinParamsReady,
    setSupinParams,
  ]);

  // Clear the module-level store when the SUPIN surface unmounts (leaving the
  // tab entirely — NOT when just the "Inlet Parameters" window closes; see the
  // mount sites in FeatureTreeSidebar, where this component's lifetime is tied
  // to the surface, independent of the window's own open/closed state).
  useEffect(() => () => resetSupinFields(), []);

  return null;
}

export function AirContent({ enabled }: { enabled: boolean }) {
  const { supinInletType, bumpDomainParams } = useHomepage();
  const {
    mach,
    altitude,
    qdyn,
    engineFlow,
    rampStages,
    rampAngle,
    coneAngle,
    numStages,
    parentMach,
    kstex,
    cfdGrids,
    setField,
  } = useSupinFields();
  const setMach = (v: string) => setField('mach', v);
  const setAltitude = (v: string) => setField('altitude', v);
  const setQdyn = (v: string) => setField('qdyn', v);
  const setEngineFlow = (v: string) => setField('engineFlow', v);
  const setRampStages = (v: string) => setField('rampStages', v);
  const setRampAngle = (v: string) => setField('rampAngle', v);
  const setConeAngle = (v: string) => setField('coneAngle', v);
  const setNumStages = (v: string) => setField('numStages', v);
  const setParentMach = (v: string) => setField('parentMach', v);
  const setKstex = (v: string) => setField('kstex', v);
  const setCfdGrids = (v: boolean) => setField('cfdGrids', v);

  // K5 uses dynamic pressure (Kfs=4), everything else uses altitude (Kfs=1).
  const usesQdyn = supinInletType === 'K5';

  const field = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    unit: string,
  ) => (
    <div className="flex items-center gap-2">
      <span className="flex-1 text-xs text-telekinesis-neutral-50">
        {label}
      </span>
      <Input
        type="number"
        step="any"
        disabled={!enabled}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          bumpDomainParams();
        }}
        className="h-7 w-20 rounded-md px-2 text-center text-xs text-[#107d40]"
      />
      <span className="w-12 text-right text-xs text-telekinesis-neutral-50">
        {unit}
      </span>
    </div>
  );

  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-telekinesis-neutral-50">
        NASA SUPIN Supersonic Inlets
      </p>
      <div className="space-y-3">
        {supinInletType !== '' && (
          <>
            {field('Mach', mach, setMach, '[-]')}
            {usesQdyn
              ? field('Dyn. Pressure', qdyn, setQdyn, '[psf]')
              : field('Altitude', altitude, setAltitude, '[ft]')}
            {field('Engine Flow', engineFlow, setEngineFlow, '[lb/s]')}
            {(supinInletType === 'K2' || supinInletType === 'K4') && (
              <>
                {field('Ramp Stages', rampStages, setRampStages, '[-]')}
                {field('First Ramp Angle', rampAngle, setRampAngle, '[°]')}
              </>
            )}
            {supinInletType === 'K3' && (
              <>
                {field('Cone Half-Angle', coneAngle, setConeAngle, '[°]')}
                {field('Cone Stages', numStages, setNumStages, '[-]')}
              </>
            )}
            {supinInletType === 'K5' && (
              <>
                {field('Parent Mach (Mstex)', parentMach, setParentMach, '[-]')}
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-xs text-telekinesis-neutral-50">
                    Parent Flow
                  </span>
                  <Select
                    disabled={!enabled}
                    value={kstex}
                    onValueChange={(v) => {
                      setKstex(v);
                      bumpDomainParams();
                    }}
                  >
                    <SelectTrigger className="h-7 w-36 rounded-md border border-telekinesis-neutral-700 bg-telekinesis-neutral-800 px-2 text-xs text-telekinesis-neutral-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-telekinesis-neutral-700 bg-telekinesis-neutral-800 text-telekinesis-neutral-50">
                      <SelectItem value="0" className="text-xs">
                        Busemann ICFA
                      </SelectItem>
                      <SelectItem value="1" className="text-xs">
                        Internal-Conical
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="flex items-center gap-2 pt-1">
              <label className="flex-1 text-xs text-telekinesis-neutral-50">
                Include CFD grids
              </label>
              <input
                type="checkbox"
                disabled={!enabled}
                checked={cfdGrids}
                onChange={(e) => {
                  setCfdGrids(e.target.checked);
                  bumpDomainParams();
                }}
                className="h-4 w-4"
              />
              <span className="w-12 text-right text-xs text-telekinesis-neutral-50">
                {cfdGrids ? 'on' : 'off'}
              </span>
            </div>
          </>
        )}

        {supinInletType === '' && (
          <p className="text-xs text-telekinesis-text-secondary">
            Select an inlet type above to configure parameters.
          </p>
        )}
      </div>
    </div>
  );
}
